import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { environment } from '../environments/environment';
import type { MembershipKind } from './membership';

const VIP_ROLE = 'vip';
const MEMBER_ROLE = 'member';
const ADMIN_ROLE = 'admin';

const SYSTEM_REALM_ROLES = new Set(['offline_access', 'uma_authorization']);

export const GUEST_MEMBERSHIP_LEVEL = -1;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private membershipLevel: number | null = null;
  private maxPersonalCollections: number | null = null;

  private readonly keycloak = new Keycloak({
    url: environment.keycloak.url,
    realm: environment.keycloak.realm,
    clientId: environment.keycloak.clientId
  });

  async initialize(): Promise<boolean> {
    try {
      return await this.keycloak.init({
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        silentCheckSsoFallback: false,
        pkceMethod: 'S256',
        checkLoginIframe: false
      });
    } catch {
      return false;
    }
  }

  async login(): Promise<void> {
    await this.keycloak.login();
  }

  async logout(): Promise<void> {
    this.membershipLevel = null;
    this.maxPersonalCollections = null;
    await this.keycloak.logout({
      redirectUri: window.location.origin + '/home'
    });
  }

  async getToken(): Promise<string> {
    if (!this.keycloak.authenticated) {
      throw new Error('User is not authenticated');
    }

    await this.keycloak.updateToken(30);
    const token = this.keycloak.token;
    if (!token) {
      throw new Error('Token is missing');
    }

    return token;
  }

  isAuthenticated(): boolean {
    return !!this.keycloak.authenticated;
  }

  getPreferredUsername(): string | undefined {
    const parsed = this.keycloak.tokenParsed as { preferred_username?: string } | undefined;
    return parsed?.preferred_username;
  }

  getSubject(): string | undefined {
    const parsed = this.keycloak.tokenParsed as { sub?: string } | undefined;
    return parsed?.sub;
  }

  getDisplayName(): string {
    if (!this.keycloak.authenticated) {
      return '';
    }
    const p = this.keycloak.tokenParsed as
      | { name?: string; given_name?: string; family_name?: string; preferred_username?: string; email?: string }
      | undefined;
    if (p?.name?.trim()) {
      return p.name.trim();
    }
    if (p?.given_name || p?.family_name) {
      return [p.given_name, p.family_name].filter(Boolean).join(' ').trim();
    }
    return p?.preferred_username ?? p?.email ?? 'User';
  }

  getEmail(): string | undefined {
    const p = this.keycloak.tokenParsed as { email?: string } | undefined;
    return p?.email;
  }

  getProfilePictureUrl(): string | undefined {
    const p = this.keycloak.tokenParsed as { picture?: string } | undefined;
    return typeof p?.picture === 'string' && p.picture.length > 0 ? p.picture : undefined;
  }

  getDisplayInitials(): string {
    const name = this.getDisplayName();
    if (!name) {
      return '?';
    }
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0]!.slice(0, 1) + parts[parts.length - 1]!.slice(0, 1)).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }

  getMembershipKind(): MembershipKind {
    if (!this.keycloak.authenticated) {
      return 'guest';
    }

    const roles = this.getEffectiveRoles();
    if (roles.includes(VIP_ROLE)) {
      return 'vip';
    }

    return 'member';
  }

  isVip(): boolean {
    return this.getMembershipKind() === 'vip';
  }

  setMembershipLevel(level: number): void {
    this.membershipLevel = Number.isFinite(level) ? level : 0;
  }

  setMaxPersonalCollections(max: number): void {
    this.maxPersonalCollections = Number.isFinite(max) && max >= 0 ? max : 0;
  }

  getViewerMembershipLevel(): number {
    if (!this.keycloak.authenticated) {
      return GUEST_MEMBERSHIP_LEVEL;
    }

    return this.membershipLevel ?? 0;
  }

  getMaxPersonalCollections(): number {
    if (!this.keycloak.authenticated) {
      return 0;
    }

    return this.maxPersonalCollections ?? 0;
  }

  isRegularMember(): boolean {
    return this.getMembershipKind() === 'member';
  }

  isAdmin(): boolean {
    return this.getEffectiveRoles().includes(ADMIN_ROLE);
  }

  hasExplicitMemberRole(): boolean {
    return this.getEffectiveRoles().includes(MEMBER_ROLE);
  }

  openAccountManagement(): void {
    const a = document.createElement('a');
    a.href = `${environment.keycloak.url}/realms/${environment.keycloak.realm}/account`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  }

  private getEffectiveRoles(): string[] {
    const parsed = this.keycloak.tokenParsed as
      | {
          realm_access?: { roles?: string[] };
          resource_access?: Record<string, { roles?: string[] }>;
        }
      | undefined;

    if (!parsed) {
      return [];
    }

    const realm = (parsed.realm_access?.roles ?? []).filter(
      (r) => !r.startsWith('default-roles-') && !SYSTEM_REALM_ROLES.has(r)
    );

    const clientId = environment.keycloak.clientId;
    const client = parsed.resource_access?.[clientId]?.roles ?? [];

    return [...new Set([...realm, ...client])];
  }
}
